import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Tag, ArrowRight, ShoppingBag } from "lucide-react";
import { inr, offerNameLabel, FALLBACK_IMG } from "@/lib/api";
import FreeDeliveryNudge from "@/components/FreeDeliveryNudge";
import { useSeo } from "@/lib/seo";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";

export default function CartPage() {
  useSeo({ title: "Your Shopping Bag | Soonyoung", noindex: true, path: "/cart" });
  const { isAuthed, setLoginOpen, cartData, updateQty, removeCartItem, applyCoupon, removeCoupon } = useStore();
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const items = cartData?.cart?.items || [];
  const totals = cartData?.totals;

  if (!isAuthed) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-4 pt-28 text-center" data-testid="cart-login-prompt">
        <ShoppingBag className="h-10 w-10 text-gold" />
        <h1 className="mt-4 font-display text-3xl text-cream">Sign in to see your bag</h1>
        <p className="mt-2 text-sm text-sage">Your bag lives on your account — OTP sign-in takes 10 seconds.</p>
        <button data-testid="cart-login-btn" onClick={() => setLoginOpen(true)} className="mt-6 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest">Sign in with OTP</button>
      </div>
    );
  }

  return (
    <section data-testid="cart-page" className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32">
      <Reveal>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Your Selection</p>
        <h1 className="mt-2 font-display text-3xl tracking-tight text-cream sm:text-5xl">The Shopping Bag</h1>
      </Reveal>

      {items.length === 0 ? (
        <div className="mt-12 flex flex-col items-center rounded-3xl border border-dashed border-champagne/60 py-24 text-center" data-testid="cart-empty">
          <p className="font-display text-2xl text-cream">Your bag is empty</p>
          <p className="mt-2 text-sm text-sage">Fill it with something beautiful.</p>
          <Link to="/shop" data-testid="cart-shop-link" className="mt-6 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest">Shop the Shelf</Link>
        </div>
      ) : (
        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_380px]">
          <ul className="divide-y divide-champagne/30">
            {items.map((it) => (
              <li key={it.id} data-testid={`cart-line-${it.id}`} className="flex gap-4 py-5 sm:gap-6">
                <Link to={`/product/${it.product_id}`} className="h-28 w-24 shrink-0 overflow-hidden rounded-2xl border border-champagne/40 bg-moss">
                  <img src={it.image || it.primary_image || it.thumbnail || FALLBACK_IMG} onError={(e) => { if (!e.currentTarget.dataset.fbk) { e.currentTarget.dataset.fbk = "1"; e.currentTarget.src = FALLBACK_IMG; } }} alt={it.name} className="h-full w-full object-cover" />
                </Link>
                <div className="flex flex-1 flex-col">
                  <p className="line-clamp-2 font-display text-base text-cream sm:text-lg">{it.name}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fern">SKU {it.sku}</p>
                  {it.offer_name && (
                    <p data-testid={`cart-offer-${it.id}`} className="mt-1.5 inline-flex w-fit items-center rounded-full bg-black px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white">
                      Offer · {offerNameLabel(it.offer_name)}
                    </p>
                  )}
                  <div className="mt-auto flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-4 rounded-full border border-champagne/50 px-3 py-1.5">
                      <button data-testid={`cart-dec-${it.id}`} onClick={() => updateQty(it.id, it.qty - 1)} className="text-sage hover:text-gold" aria-label="Decrease"><Minus className="h-3.5 w-3.5" /></button>
                      <span data-testid={`cart-qty-${it.id}`} className="w-5 text-center font-mono text-sm font-bold text-cream">{it.qty}</span>
                      <button data-testid={`cart-inc-${it.id}`} onClick={() => updateQty(it.id, it.qty + 1)} className="text-sage hover:text-gold" aria-label="Increase"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-lg text-cream">{inr(it.line_total)}</p>
                      <p className="font-mono text-[10px] text-fern">{inr(it.selling_price)} each × {it.qty}</p>
                      {it.list_price > it.selling_price && (
                        <p className="text-xs text-fern line-through">{inr(it.list_price * it.qty)}</p>
                      )}
                      {it.offer_discount > 0 && (
                        <p data-testid={`cart-save-${it.id}`} className="mt-1 inline-block rounded-full bg-gold/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-gold">
                          Offer savings {inr(it.offer_discount)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <button data-testid={`cart-remove-${it.id}`} onClick={() => removeCartItem(it.id)} className="self-start text-fern hover:text-gold" aria-label="Remove item">
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>

          <aside className="h-fit rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="cart-summary">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Order Summary</p>
            <div className="mt-4">
              <FreeDeliveryNudge totals={totals} items={items} />
            </div>

            {totals?.coupon_code ? (
              <div className="mt-4 flex items-center justify-between rounded-xl bg-moss px-4 py-3">
                <span className="flex items-center gap-2 font-mono text-xs text-cream"><Tag className="h-3.5 w-3.5 text-gold" /> {totals.coupon_code}</span>
                <button data-testid="coupon-remove-btn" onClick={removeCoupon} className="font-mono text-[10px] uppercase tracking-widest text-gold hover:underline">Remove</button>
              </div>
            ) : !totals?.has_product_offers && (
              <div className="mt-4 flex gap-2">
                <input
                  data-testid="coupon-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Coupon code"
                  className="w-full rounded-full border border-champagne/50 bg-forest px-4 py-2.5 text-xs text-cream placeholder:text-fern focus:border-gold focus:outline-none"
                />
                <button data-testid="coupon-apply-btn" onClick={() => code.trim() && applyCoupon(code.trim().toUpperCase())} className="rounded-full border border-gold/50 px-5 font-mono text-[10px] uppercase tracking-widest text-gold hover:bg-gold hover:text-forest">
                  Apply
                </button>
              </div>
            )}

            <div className="mt-5 space-y-2.5 text-sm">
              <div className="flex justify-between text-sage"><span>MRP total</span><span>{inr(totals?.subtotal_mrp)}</span></div>
              {(totals?.product_discount || 0) - (totals?.offer_discount || 0) > 0 && (
                <div className="flex justify-between text-gold"><span>Product savings</span><span>-{inr((totals?.product_discount || 0) - (totals?.offer_discount || 0))}</span></div>
              )}
              {totals?.offer_discount > 0 && (
                <div className="flex justify-between font-bold text-gold" data-testid="offer-savings-line"><span>Offer savings</span><span>-{inr(totals.offer_discount)}</span></div>
              )}
              {totals?.coupon_discount > 0 && (
                <div className="flex justify-between text-gold" data-testid="coupon-discount-line"><span>Coupon</span><span>-{inr(totals.coupon_discount)}</span></div>
              )}
              {(totals?.product_discount > 0 || totals?.coupon_discount > 0) && (
                <div className="flex justify-between rounded-xl bg-gold/10 px-3 py-2 font-bold text-gold" data-testid="total-savings-line">
                  <span>Total savings</span>
                  <span>-{inr((totals?.product_discount || 0) + (totals?.coupon_discount || 0))}</span>
                </div>
              )}
              <div className="flex justify-between text-sage"><span>Shipping</span><span className="font-mono text-[10px] uppercase tracking-widest text-fern" data-testid="cart-shipping-note">At checkout · by pincode</span></div>
              <div className="flex justify-between border-t border-champagne/30 pt-3 text-base font-bold text-cream">
                <span>Payable</span><span data-testid="cart-payable">{inr(totals?.payable)}</span>
              </div>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-fern">Shipping calculated at checkout</p>
            </div>

            <button
              data-testid="proceed-checkout-btn"
              onClick={() => navigate("/checkout")}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-transform hover:scale-[1.02]"
            >
              Proceed to Checkout <ArrowRight className="h-4 w-4" />
            </button>
          </aside>
        </div>
      )}
    </section>
  );
}
